import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import DailyIframe from "@daily-co/daily-js";
import { Button, Typography, Spin, Input, Tag, Space, Alert } from "antd";
import { SnippetsOutlined } from "@ant-design/icons";
import axios from "axios";
import RequestPremium from "./RequestPremium";

const { Text } = Typography;

/**
 * Simple Daily.co video conferencing component using Prebuilt UI.
 * - Creates a room via backend (/api/daily/room)
 * - Joins the room in an iframe
 * - Cleans up on unmount
 */
export default function VideoConf({ onClose, onSessionUrlChange, leaveSignal = 0, onRequestPremium }) {
  const containerRef = useRef(null);
  const callRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | requesting-room | joining | joined | error
  const [error, setError] = useState(null);
  const [roomUrl, setRoomUrl] = useState(null);
  const [joinUrl, setJoinUrl] = useState("");
  const [debouncedJoin, setDebouncedJoin] = useState("");
  const [baseUrl, setBaseUrl] = useState("https://simplyride.daily.co"); // default Daily domain
  const [joinError, setJoinError] = useState("");
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  // null = unknown (still loading or failed to fetch); boolean when known
  const [isPremium, setIsPremium] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [creditsFetchAttempted, setCreditsFetchAttempted] = useState(false);
  const premiumModalRef = useRef(null);

  // Axios instance with auth header + 401 refresh-and-retry (mirrors SaveTraining.js)
  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: "/api",
      headers: { "Content-Type": "application/json" },
    });
    instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers = config.headers || {};
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const refreshToken = localStorage.getItem("refresh_token");
            const refreshResponse = await axios.post("/api/refresh_tokens/", { refresh_token: refreshToken });
            if (refreshResponse.status === 200) {
              const { access_token: newToken, refresh_token: newRefreshToken } = refreshResponse.data;
              localStorage.setItem("token", newToken);
              localStorage.setItem("refresh_token", newRefreshToken);
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            // Keep tokens as-is so user can still access local data; surface error via caller
            // eslint-disable-next-line no-console
            console.error("Failed to refresh token:", refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, []);

  const openPremiumModal = useCallback((context) => {
    try {
      premiumModalRef.current?.open?.({ context: context ? String(context) : "" });
    } catch {
      // ignore
    }
  }, []);

  // Treat "-/unknown" credits as 0 (per requirement).
  const creditsRemainingSafe = useMemo(() => {
    const n = creditsRemaining == null ? 0 : typeof creditsRemaining === "number" ? creditsRemaining : Number(creditsRemaining);
    return Number.isFinite(n) ? n : 0;
  }, [creditsRemaining]);

  const isOutOfLiveCredits = useMemo(() => {
    // Show the premium request UX even for admins (per requirement).
    // Only skip when explicitly premium.
    if (isPremium === true) return false;
    return creditsRemainingSafe <= 0;
  }, [creditsRemainingSafe, isPremium]);

  const refreshCredits = useMemo(() => {
    return async () => {
      setCreditsLoading(true);
      setCreditsFetchAttempted(true);
      try {
        const resp = await axiosInstance.post("/daily/credits/", {});
        const data = resp?.data || {};
        setCreditsRemaining(typeof data.credits_remaining === "number" ? data.credits_remaining : data.credits_remaining == null ? null : Number(data.credits_remaining));
        if (typeof data.is_premium === "boolean") {
          setIsPremium(data.is_premium);
        } else {
          // Fallback: determine premium from user profile if credits endpoint didn't return it.
          try {
            const u = await axiosInstance.post("/get_user_info/", {});
            const ud = u?.data || {};
            if (typeof ud?.is_premium === "boolean") setIsPremium(ud.is_premium);
          } catch {
            // keep unknown
          }
        }
        setIsAdmin(!!data.is_admin);
      } catch {
        // If it fails, keep feature working and let backend enforce on create.
        setCreditsRemaining(null);
        // Fallback: premium can be known even if credits fetch failed.
        try {
          const u = await axiosInstance.post("/get_user_info/", {});
          const ud = u?.data || {};
          if (typeof ud?.is_premium === "boolean") setIsPremium(ud.is_premium);
          else setIsPremium(null);
        } catch {
          setIsPremium(null);
        }
      } finally {
        setCreditsLoading(false);
      }
    };
  }, [axiosInstance]);

  // Fetch credits status on mount so we can show remaining credits before creating a room.
  useEffect(() => {
    void refreshCredits();
  }, [refreshCredits]);

  // Live-validate join input (debounced) and surface inline error
  useEffect(() => {
    const val = (debouncedJoin || "").trim();
    if (!val) {
      setJoinError("");
      return;
    }
    if (/^https?:\/\//i.test(val)) {
      try {
        // eslint-disable-next-line no-new
        new URL(val);
        setJoinError("");
      } catch {
        setJoinError("Invalid room ID");
      }
      return;
    }
    if (/^[a-z]{3}-[a-z]{3}$/i.test(val)) {
      setJoinError("");
    } else {
      setJoinError("Invalid room ID");
    }
  }, [debouncedJoin]);

  const isJoinInputValid = useMemo(() => {
    const val = (debouncedJoin || "").trim();
    if (!val) return false;
    if (/^https?:\/\//i.test(val)) {
      try {
        // Validate URL format
        // eslint-disable-next-line no-new
        new URL(val);
        return true;
      } catch {
        return false;
      }
    }
    return /^[a-z]{3}-[a-z]{3}$/i.test(val);
  }, [debouncedJoin]);

  // Cleanup on unmount: destroy only to avoid postMessage on removed iframe
  useEffect(() => {
    return () => {
      const frame = callRef.current;
      callRef.current = null;
      if (frame) {
        try { frame.destroy(); } catch {}
      }
    };
  }, []);

  // Load stored Daily base URL if we have one
  useEffect(() => {
    try {
      const stored = localStorage.getItem("dailyBaseUrl");
      if (stored) setBaseUrl(stored);
    } catch {}
  }, []);

  // Debounce join input by 0.5s before validating/enabling join
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedJoin(joinUrl);
    }, 500);
    return () => clearTimeout(handle);
  }, [joinUrl]);

  // Load and persist join input across mounts (tab switches)
  useEffect(() => {
    try {
      const storedJoin = localStorage.getItem("dailyJoinUrl");
      const storedDate = localStorage.getItem("dailyJoinDate");
      const today = new Date().toISOString().slice(0, 10);
      if (storedJoin && storedDate && storedDate === today) {
        setJoinUrl(storedJoin);
      } else {
        setJoinUrl("");
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const val = (joinUrl || "").trim();
      if (val) {
        localStorage.setItem("dailyJoinUrl", val);
      } else {
        localStorage.removeItem("dailyJoinUrl");
      }
    } catch {}
  }, [joinUrl]);

  const leaveMeeting = async () => {
      // Capture current session id to prefill join box
      let lastId = null;
      try {
        if (roomUrl) {
          const u = new URL(roomUrl);
          lastId = u.pathname.split("/").filter(Boolean).pop() || null;
        }
      } catch {}
      // If there's no active frame, just reset UI
      const frame = callRef.current;
      callRef.current = null;
      try {
        if (frame) {
        try { await frame.leave(); } catch {}
        try { frame.destroy(); } catch {}
        }
      } finally {
        setRoomUrl(null);
        setError(null);
      setJoinError("");
        setStatus("idle");
        if (lastId) {
          setJoinUrl(lastId.toLowerCase());
        }
        if (typeof onSessionUrlChange === "function") onSessionUrlChange(null);
      }
    };

  // Leave request coming from parent (header button)
  useEffect(() => {
    if (leaveSignal > 0) {
      void leaveMeeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveSignal]);

  const createRoomAndJoin = async () => {
    try {
      // Frontend guard: only block members when we definitively know they have 0 remaining.
      if (!isAdmin && isPremium !== true && creditsRemainingSafe <= 0) {
        setError("You’ve used all your live session credits for this month. Upgrade to Premium for unlimited live sessions.");
        setStatus("error");
        openPremiumModal("Live session credits used up");
        return;
      }
      setError(null);
      setStatus("requesting-room");
      const { data } = await axiosInstance.post("/daily/room/", {});
      try {
        if (typeof data?.credits_remaining === "number") setCreditsRemaining(data.credits_remaining);
        if (typeof data?.is_premium === "boolean") setIsPremium(data.is_premium);
        if (typeof data?.is_admin === "boolean") setIsAdmin(data.is_admin);
      } catch {}
      // Refresh tokens if backend provided new ones (same pattern as getAllTrainingPlansInfo)
      try {
        if (data?.refresh_token) {
          localStorage.setItem("token", data.access_token);
          localStorage.setItem("refresh_token", data.refresh_token);
        }
      } catch {}
      setRoomUrl(data.url);
      try {
        const origin = new URL(data.url).origin;
        setBaseUrl(origin);
        try { localStorage.setItem("dailyBaseUrl", origin); } catch {}
      } catch {
        // ignore parse errors
      }
      if (typeof onSessionUrlChange === "function") onSessionUrlChange(data.url);
      setStatus("joining");
      const callFrame = DailyIframe.createFrame(containerRef.current, {
        iframeStyle: {
          width: "100%",
          height: "100%",
          border: "0",
          borderRadius: "12px",
        },
        showLeaveButton: true,
      });
      callRef.current = callFrame;
      await callFrame.join({ url: data.url });
      // Persist today's session id for same-day prefill
      try {
        const today = new Date().toISOString().slice(0, 10);
        const lastId = (() => {
          try {
            const u = new URL(data.url);
            return u.pathname.split("/").filter(Boolean).pop() || "";
          } catch {
            return "";
          }
        })();
        if (lastId) {
          localStorage.setItem("dailyJoinUrl", lastId.toLowerCase());
          localStorage.setItem("dailyJoinDate", today);
        }
      } catch {}
      setStatus("joined");
    } catch (e) {
      const statusCode = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const detailObj = detail && typeof detail === "object" ? detail : null;
      const detailMsg = detailObj ? (detailObj.message || detailObj.detail || "") : "";
      try {
        if (detailObj && typeof detailObj.credits_remaining === "number") setCreditsRemaining(detailObj.credits_remaining);
        if (detailObj && typeof detailObj.is_premium === "boolean") setIsPremium(detailObj.is_premium);
      } catch {}
      if (statusCode === 429) {
        setError(detailMsg || (typeof detail === "string" ? detail : "") || "You’ve exceeded your monthly live session quota. Upgrade to Premium for unlimited live sessions.");
        openPremiumModal("Live sessions quota exceeded (429)");
      } else {
        setError(detailMsg || (typeof detail === "string" ? detail : "") || e.message || "Unable to start the call");
      }
      setStatus("error");
      if (typeof onSessionUrlChange === "function") onSessionUrlChange(null);
    }
  };

  const joinExistingMeeting = async () => {
    try {
      setJoinError("");
      const val = (joinUrl || "").trim();
      let urlToJoin = null;
      if (/^https?:\/\//i.test(val)) {
        urlToJoin = val;
      } else if (/^[a-z]{3}-[a-z]{3}$/i.test(val)) {
        urlToJoin = `${baseUrl}/${val.toLowerCase()}`;
      } else {
        setJoinError("Invalid room ID");
        return;
      }
      setError(null);
      setStatus("joining");
      // Inform parent immediately so controls (copy/leave) appear even before join completes
      if (typeof onSessionUrlChange === "function") onSessionUrlChange(urlToJoin);
      // Ensure a fresh frame
      if (callRef.current) {
        try { callRef.current.leave(); } catch {}
        try { callRef.current.destroy(); } catch {}
        callRef.current = null;
      }
      const callFrame = DailyIframe.createFrame(containerRef.current, {
        iframeStyle: {
          width: "100%",
          height: "100%",
          border: "0",
          borderRadius: "12px",
        },
        showLeaveButton: true,
      });
      callRef.current = callFrame;
      await callFrame.join({ url: urlToJoin });
      setRoomUrl(urlToJoin);
      try {
        const origin = new URL(urlToJoin).origin;
        setBaseUrl(origin);
        try { localStorage.setItem("dailyBaseUrl", origin); } catch {}
      } catch {}
      // Persist today's session id for same-day prefill
      try {
        const today = new Date().toISOString().slice(0, 10);
        const lastId = (() => {
          try {
            const u = new URL(urlToJoin);
            return u.pathname.split("/").filter(Boolean).pop() || "";
          } catch {
            return "";
          }
        })();
        if (lastId) {
          localStorage.setItem("dailyJoinUrl", lastId.toLowerCase());
          localStorage.setItem("dailyJoinDate", today);
        }
      } catch {}
      setStatus("joined");
    } catch (e) {
      // Small inline error for invalid/non-existing room
      setJoinError("Invalid room ID");
      // Clean up any half-initialized frame
      try {
        if (callRef.current) {
          try { callRef.current.destroy(); } catch {}
          callRef.current = null;
        }
      } catch {}
      setStatus("idle");
      if (typeof onSessionUrlChange === "function") onSessionUrlChange(null);
    }
  };

  // Parent controls leaving by unmounting the component

  return (
    <div style={{ width: "100%", height: "100%", marginTop: 0 }}>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <RequestPremium axiosInstance={axiosInstance} hideButton ref={premiumModalRef} />
        <div
          ref={containerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.05)",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            overflow: "hidden",
          }}
        />

        {status === "idle" && !roomUrl && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
            }}
          >
            <div style={{ width: "100%", maxWidth: 560, padding: 16, textAlign: "center" }}>
              <div style={{ marginBottom: 12 }}>
                <Text strong>Join live session</Text>
                <div style={{ height: 8 }} />
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Button
                    aria-label="Paste meeting ID"
                    onClick={async () => {
                      try {
                        setJoinError("");
                        const text = await navigator.clipboard.readText();
                        if (!text) return;
                        const trimmed = text.trim();
                        if (/^[a-z]{6}$/i.test(trimmed)) {
                          const normalized = `${trimmed.slice(0,3)}-${trimmed.slice(3)}`.toLowerCase();
                          setJoinUrl(normalized);
                          return;
                        }
                        const idMatch = trimmed.match(/([a-z]{3}-[a-z]{3})/i);
                        if (idMatch) {
                          setJoinUrl(idMatch[1].toLowerCase());
                        } else {
                          setJoinUrl(trimmed);
                        }
                      } catch {}
                    }}
                    className="primary-glass-btn"
                    icon={<SnippetsOutlined />}
                  />
                  <Input
                    placeholder="Enter session ID (e.g., abc-def)"
                    value={joinUrl}
                    onChange={(e) => {
                      const raw = e.target.value || "";
                      const val = raw.trim();
                      if (/^[a-z]{6}$/i.test(val)) {
                        const normalized = `${val.slice(0,3)}-${val.slice(3)}`.toLowerCase();
                        setJoinUrl(normalized);
                      } else {
                        setJoinUrl(raw);
                      }
                      setJoinError("");
                    }}
                  />
                  <Button
                    type="primary"
                    className="primary-glass-btn"
                    disabled={!isJoinInputValid || status === "joining" || status === "requesting-room"}
                    onClick={joinExistingMeeting}
                  >
                    Join
                  </Button>
                </div>
                <div style={{ marginTop: 8, minHeight: 18 }}>
                  {joinError ? (
                    <Text type="danger" style={{ fontSize: 12 }}>{joinError}</Text>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Enter session ID like abc-def.
                    </Text>
                  )}
                </div>
              </div>
              <div style={{ opacity: 0.7, margin: "8px 0", fontWeight: 600 }}>OR</div>
              
              <Button
                type="primary"
                size="large"
                className="primary-glass-btn"
                onClick={createRoomAndJoin}
                disabled={!isAdmin && isPremium !== true && creditsRemainingSafe <= 0}
              >
                Create new live session (max 1h)
              </Button>
              {/* Credits/premium indicator (create quota only; joining unaffected) */}
              <div style={{ marginTop: 10 }}>
                {creditsLoading || !creditsFetchAttempted ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Checking premium status and live session credits…
                  </Text>
                ) : isPremium === true ? (
                  <Tag color="gold">Unlimited live sessions</Tag>
                ) : (
                  <Space direction="vertical" size={6}>
                    <Tag color={creditsRemainingSafe <= 0 ? "red" : "blue"}>
                      Live sessions remaining this month: {Math.max(0, Number(creditsRemainingSafe) || 0)}
                    </Tag>
                    {/* Show even if admin (per requirement) */}
                    <RequestPremium
                      axiosInstance={axiosInstance}
                      buttonText="Upgrade to Premium (Unlimited)"
                      buttonProps={{ style: { background: "#faad14", borderColor: "#faad14", color: "#111", fontWeight: 600 } }}
                      context="Live sessions (VideoConf)"
                    />
                  </Space>
                )}
              </div>
            </div>
          </div>
        )}
        {status === "requesting-room" && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <Spin />
            <Text type="secondary">
              Creating room...
            </Text>
          </div>
        )}
        {status === "error" && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text type="danger">{error}</Text>
          </div>
        )}
      </div>
    </div>
  );
}

